import { FC, createContext, useContext, useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import moment from 'moment';

import { DataPaginateResponse } from '~globals/types/api';
import { Nullable } from '~globals/types/commons';
import { DriverCompanyBase } from '~services/driver/types';
import { getDriverCompanyAvailables } from '~services/driver';
import { AuthExtendedCompany } from '~services/auth/types';
import { getAssociatedCompanies } from '~services/partnerCompany';
import {
  AdminReportSummary,
  AdminReportStatistic,
  AdminReportSummaryFilter,
} from '~services/admin/report/types';
import { getReportSummary, getReportStatistic } from '~services/admin/report';
import { selectAuth } from '~store/slices/auth/index';
import {
  useRequest,
  useTypePartnerCompany,
  useAppSelector,
} from '~hooks/index';

import { CURRENT_PERIOD_MONTH } from '../utils';

import { ReportsVisitsSectionContextValue } from './types';

const ReportsVisitsSectionContext =
  createContext<ReportsVisitsSectionContextValue>({
    filters: {
      currentPeriod: {
        value: CURRENT_PERIOD_MONTH,
        setValue: () => {},
      },
      currentDriversIds: {
        value: null,
        setValue: () => {},
      },
      currentAssociatedCompanyId: {
        value: null,
        setValue: () => {},
      },
      selectsData: {
        availablesDrivers: {
          data: [],
          loading: true,
        },
        associatedCompanies: {
          data: [],
          loading: true,
        },
      },
    },
    data: {
      summary: {
        data: null,
        loading: true,
      },
      statistic: {
        data: null,
        loading: true,
      },
    },
  });

export const useReportsVisitsSectionContext =
  (): ReportsVisitsSectionContextValue =>
    useContext(ReportsVisitsSectionContext);

const ReportsVisitsSectionContextProvider: FC = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { isCarrier } = useTypePartnerCompany();
  const { user } = useAppSelector(selectAuth);

  const [currentPeriod, setCurrentPeriod] =
    useState<Nullable<Date>>(CURRENT_PERIOD_MONTH);

  const [currentDriversIds, setCurrentDriversIds] =
    useState<Nullable<string[]>>(null);

  const [currentAssociatedCompanyId, setCurrentAssociatedCompanyId] = useState<
    Nullable<string>
  >(isCarrier ? user?.companyId ?? null : null);

  const [availablesDrivers, setAvailablesDrivers] = useState<
    DriverCompanyBase[]
  >([]);
  const [, loadingAvailablesDrivers] = useRequest({
    request: getDriverCompanyAvailables,
    payload: null,
    withPostSuccess: (response) => {
      const availablesDriversResponse = response.data as DataPaginateResponse<
        DriverCompanyBase[]
      >;

      setAvailablesDrivers(availablesDriversResponse.data.results);
    },
  });

  const [associatedCompanies, setAssociatedCompanies] = useState<
    AuthExtendedCompany[]
  >([]);
  const [, loadingAssocitedCompanies] = useRequest({
    request: getAssociatedCompanies,
    payload: null,
    withPostSuccess: (response) => {
      const associatedCompaniesResponse = response.data?.data
        .results as AuthExtendedCompany[];

      setAssociatedCompanies(associatedCompaniesResponse);
    },
  });

  const [summaryData, setSummaryData] =
    useState<Nullable<AdminReportSummary>>(null);
  const payloadGetReportSummary = useMemo<AdminReportSummaryFilter>(() => {
    const current = moment(currentPeriod);

    return {
      month: current.get('month') + 1,
      year: current.get('year'),
      driverIds: currentDriversIds,
      associatedCompanyId: currentAssociatedCompanyId,
    };
  }, [currentPeriod, currentDriversIds, currentAssociatedCompanyId]);
  const [, loadingSummaryData] = useRequest(
    {
      initialState: null,
      request: getReportSummary,
      payload: payloadGetReportSummary,
      withPostSuccess: (response) => {
        const summaryResponse = response.data?.data as AdminReportSummary;

        setSummaryData(summaryResponse);
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
      },
    },
    [payloadGetReportSummary],
  );

  const [statisticData, setStatisticData] =
    useState<Nullable<AdminReportStatistic>>(null);
  const [, loadingStatisticsData] = useRequest(
    {
      initialState: null,
      request: getReportStatistic,
      payload: { driverIds: currentDriversIds },
      withPostSuccess: (response) => {
        const statisticResponse = response.data?.data as AdminReportStatistic;

        setStatisticData(statisticResponse);
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
      },
    },
    [currentDriversIds],
  );

  return (
    <ReportsVisitsSectionContext.Provider
      value={{
        filters: {
          currentPeriod: {
            value: currentPeriod,
            setValue: setCurrentPeriod,
          },
          currentDriversIds: {
            value: currentDriversIds,
            setValue: setCurrentDriversIds,
          },
          currentAssociatedCompanyId: {
            value: currentAssociatedCompanyId,
            setValue: setCurrentAssociatedCompanyId,
          },
          selectsData: {
            availablesDrivers: {
              data: availablesDrivers,
              loading: loadingAvailablesDrivers,
            },
            associatedCompanies: {
              data: associatedCompanies,
              loading: loadingAssocitedCompanies,
            },
          },
        },
        data: {
          summary: {
            data: summaryData,
            loading: loadingSummaryData,
          },
          statistic: {
            data: statisticData,
            loading: loadingStatisticsData,
          },
        },
      }}
    >
      {children}
    </ReportsVisitsSectionContext.Provider>
  );
};

export default ReportsVisitsSectionContextProvider;
